import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isPastTime, formatUTCDateToLocal, formatUTCTimeOnlyToLocal, calculateDuration } from '../../utils/timezone';
import './SuccessPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const HISTORY_PAGE_SIZE = 10;

interface UserData {
  firstName?: string;
  liabilityWaiverAccepted: boolean;
  liabilityWaiverExpiresAt?: string;
  canAddDependents?: boolean;
}

interface HistorySession {
  id: number;
  check_in_time: string;
  check_out_time?: string;
  work_location: string;
  task_name: string;
  is_open: boolean;
  duration_hours: number | null;
}

const formatWorkLocation = (location: string): string => {
  const normalized = (location || '').toLowerCase().trim();
  if (normalized === 'on-site' || normalized === 'onsite' || normalized === 'on_site') {
    return 'On-site';
  }
  if (normalized === 'remote') {
    return 'Remote';
  }
  return location || '—';
};

interface HistoryDayGroup {
  date: string;
  sessions: HistorySession[];
}

/** Group sessions into day blocks (local date). Within a day, oldest → newest. */
const groupSessionsByDay = (sessions: HistorySession[]): HistoryDayGroup[] => {
  const groups: HistoryDayGroup[] = [];

  for (const session of sessions) {
    const date = formatUTCDateToLocal(session.check_in_time);
    const last = groups[groups.length - 1];
    if (last && last.date === date) {
      last.sessions.push(session);
    } else {
      groups.push({ date, sessions: [session] });
    }
  }

  for (const group of groups) {
    group.sessions.sort(
      (a, b) => new Date(a.check_in_time).getTime() - new Date(b.check_in_time).getTime()
    );
  }

  return groups;
};

const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passcode = location.state?.passcode || localStorage.getItem('currentPasscode') || '';
  const [userData, setUserData] = useState<UserData | null>(null);
  const [canAddDependents, setCanAddDependents] = useState(() => {
    if (typeof location.state?.canAddDependents === 'boolean') {
      return location.state.canAddDependents;
    }
    return localStorage.getItem('canAddDependents') === 'true';
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestCategory, setRequestCategory] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestStatus, setRequestStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [requestError, setRequestError] = useState('');
  const [printStatus, setPrintStatus] = useState<'idle' | 'printing' | 'success' | 'error'>('idle');
  const [printError, setPrintError] = useState('');
  const [printCountdown, setPrintCountdown] = useState(3);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySessions, setHistorySessions] = useState<HistorySession[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const historyListRef = useRef<HTMLDivElement>(null);
  const historyOffsetRef = useRef(0);
  const historyLoadingRef = useRef(false);

  useEffect(() => {
    // Fetch data in background without blocking UI
    fetchUserData();
  }, [passcode]);

  // Auto-logout countdown after successful badge print
  useEffect(() => {
    if (printStatus === 'success' && printCountdown > 0) {
      const timer = setTimeout(() => {
        setPrintCountdown(printCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (printStatus === 'success' && printCountdown === 0) {
      localStorage.removeItem('currentPasscode');
      localStorage.removeItem('canAddDependents');
      navigate('/');
    }
  }, [printStatus, printCountdown, navigate]);

  const fetchUserData = async () => {
    if (!passcode) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/user-liability-waiver/${passcode}`);
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        if (typeof data.canAddDependents === 'boolean') {
          setCanAddDependents(data.canAddDependents);
          localStorage.setItem('canAddDependents', data.canAddDependents ? 'true' : 'false');
        }
      }
    } catch (error) {
      // Failed to fetch user data - continue without it
    }
  };

  const fetchHistoryPage = useCallback(async (offset: number, replace = false) => {
    if (!passcode || historyLoadingRef.current) return;

    historyLoadingRef.current = true;
    setHistoryLoading(true);
    setHistoryError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/checkin-history/${passcode}?limit=${HISTORY_PAGE_SIZE}&offset=${offset}`
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        setHistoryError(data.message || 'Failed to load volunteer history');
        return;
      }

      const sessions: HistorySession[] = data.sessions || [];
      setHistorySessions((prev) => (replace ? sessions : [...prev, ...sessions]));
      setHistoryTotal(data.total || 0);
      setHistoryHasMore(Boolean(data.has_more));
      historyOffsetRef.current = offset + sessions.length;
    } catch {
      setHistoryError('Failed to connect to server');
    } finally {
      historyLoadingRef.current = false;
      setHistoryLoading(false);
    }
  }, [passcode]);

  const handleOpenHistoryModal = () => {
    setShowHistoryModal(true);
    setHistorySessions([]);
    setHistoryTotal(0);
    setHistoryHasMore(false);
    setHistoryError('');
    historyOffsetRef.current = 0;
    fetchHistoryPage(0, true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
  };

  const handleHistoryScroll = () => {
    const el = historyListRef.current;
    if (!el || !historyHasMore || historyLoadingRef.current) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      fetchHistoryPage(historyOffsetRef.current, false);
    }
  };

  // If the first page doesn't fill the scroll area, keep loading until it does or there's no more.
  useEffect(() => {
    if (!showHistoryModal || historyLoading || !historyHasMore || historyError) return;
    const el = historyListRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8) {
      fetchHistoryPage(historyOffsetRef.current, false);
    }
  }, [showHistoryModal, historySessions, historyLoading, historyHasMore, historyError, fetchHistoryPage]);

  const isWaiverExpired = () => {
    if (!userData?.liabilityWaiverExpiresAt) return false;
    return isPastTime(userData.liabilityWaiverExpiresAt);
  };

  const isWaiverNotAccepted = () => {
    return userData?.liabilityWaiverAccepted === false;
  };

  const shouldShowWaiverButton = () => {
    // Don't show while loading
    if (!userData) return false;
    // Only show if expired or not accepted
    return isWaiverExpired() || isWaiverNotAccepted();
  };

  const handleLogout = () => {
    localStorage.removeItem('currentPasscode');
    localStorage.removeItem('canAddDependents');
    navigate('/');
  };

  const navigateWithPasscode = (path: string) => {
    navigate(path, { state: { passcode } });
  };

  const handleOpenRequestModal = () => {
    setShowRequestModal(true);
    setRequestCategory('');
    setRequestMessage('');
    setRequestStatus('idle');
    setRequestError('');
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
  };

  const handleSendRequest = async () => {
    if (!requestCategory) {
      setRequestError('Please select a category');
      return;
    }
    if (!requestMessage.trim()) {
      setRequestError('Please enter a message');
      return;
    }

    setRequestStatus('sending');
    setRequestError('');

    const isFeedback = requestCategory === 'Feedback';
    try {
      const response = await fetch(isFeedback ? `${API_BASE_URL}/feedback` : `${API_BASE_URL}/info-update-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          isFeedback
            ? { passcode, message: requestMessage.trim() }
            : { passcode, category: requestCategory, message: requestMessage.trim() }
        ),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRequestStatus('success');
        setTimeout(() => {
          setShowRequestModal(false);
          setRequestStatus('idle');
        }, 2000);
      } else {
        setRequestStatus('error');
        setRequestError(data.message || 'Failed to send request');
      }
    } catch (error) {
      setRequestStatus('error');
      setRequestError('Failed to connect to server');
    }
  };

  const handlePrintBadge = async () => {
    if (!passcode) {
      setPrintError('No user code available');
      setPrintStatus('error');
      return;
    }

    setPrintStatus('printing');
    setPrintError('');

    try {
      const response = await fetch(`${API_BASE_URL}/print-badge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ passcode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPrintStatus('success');
        setPrintCountdown(3);
      } else {
        setPrintStatus('error');
        setPrintError(data.message || 'Failed to print badge');
      }
    } catch (error) {
      setPrintStatus('error');
      setPrintError('Failed to connect to printer. Please try again.');
    }
  };

  const formatHours = (session: HistorySession): string => {
    if (session.is_open || !session.check_out_time) {
      return 'In progress';
    }
    return calculateDuration(session.check_in_time, session.check_out_time);
  };

  const formatSessionTimes = (session: HistorySession): string => {
    const start = formatUTCTimeOnlyToLocal(session.check_in_time);
    if (session.is_open || !session.check_out_time) {
      return `${start} – now`;
    }
    return `${start} – ${formatUTCTimeOnlyToLocal(session.check_out_time)}`;
  };

  return (
    <div className="success-container">
      <div className="success-card">
        <div className="success-icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="success-content">
          <h1 className="success-title">
            {userData?.firstName
              ? `Welcome ${userData.firstName} to FabMo's Volunteer App!`
              : "Welcome to FabMo's Volunteer App!"}
          </h1>
        </div>

        <div className="success-actions">
          <div className="action-buttons">
            <button 
              onClick={() => navigateWithPasscode('/checkin')} 
              className="action-button checkin-button"
            >
              Check In
            </button>
            {shouldShowWaiverButton() && (
              <button 
                onClick={() => navigateWithPasscode('/liability-waiver')} 
                className="action-button liability-button expired"
              >
                Update Liability Waiver (EXPIRED)
              </button>
            )}
            {printStatus === 'success' ? (
              <div className="print-success-message">
                <p className="print-success-text">Badge Printing!</p>
                <p className="print-countdown-text">Logging out in {printCountdown} seconds...</p>
              </div>
            ) : (
              <button 
                onClick={handlePrintBadge} 
                className={`action-button print-badge-button ${printStatus === 'printing' ? 'loading' : ''} ${printStatus === 'error' ? 'error' : ''}`}
                disabled={printStatus === 'printing'}
              >
                {printStatus === 'printing' ? 'Printing...' : 
                 printStatus === 'error' ? 'Print Failed - Try Again' : 
                 'Print Name Badge'}
              </button>
            )}
            {printError && <p className="print-error-message">{printError}</p>}
            {passcode && (
              <button
                type="button"
                onClick={handleOpenHistoryModal}
                className="action-button print-badge-button"
              >
                Volunteer History
              </button>
            )}
            {canAddDependents && (
              <button
                type="button"
                onClick={() => navigateWithPasscode('/dependents')}
                className="action-button print-badge-button"
              >
                Dependents
              </button>
            )}
            <button 
              onClick={handleOpenRequestModal} 
              className="action-button print-badge-button"
            >
              Contact Us
            </button>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>

        <div className="success-footer">
          <p className="footer-text">
            Thank you for all you do for FabMo!
          </p>
        </div>
      </div>

      {/* Request Info Update Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={handleCloseRequestModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Contact Us</h2>
              <button className="modal-close" onClick={handleCloseRequestModal}>×</button>
            </div>
            
            {requestStatus === 'success' ? (
              <div className="modal-success">
                <div className="success-checkmark">✓</div>
                <p>{requestCategory === 'Feedback' ? 'Thank you for your feedback!' : 'Your request has been submitted!'}</p>
              </div>
            ) : (
              <>
                <div className="modal-body">
                  <p className="modal-description">Send a message to the FabMo administrators.</p>
                  <div className="form-hints">
                    <p><strong>Emergency Contact:</strong> Update your emergency contact name, phone, or relationship</p>
                    <p><strong>Personal Information:</strong> Update your name, email, phone, or address</p>
                    <p><strong>Update Hours:</strong> Request a correction to your volunteer hours</p>
                    <p><strong>Feedback:</strong> Share a suggestion or anything you want the team to know</p>
                    <p><strong>Other:</strong> Any other changes or questions</p>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="category">Category <span className="required-star">*</span></label>
                    <select
                      id="category"
                      value={requestCategory}
                      onChange={(e) => setRequestCategory(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select a category</option>
                      <option value="Emergency Contact">Emergency Contact</option>
                      <option value="Personal Information">Personal Information</option>
                      <option value="Update Hours">Update Hours</option>
                      <option value="Feedback">Feedback</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message <span className="required-star">*</span></label>
                    <textarea
                      id="message"
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      className="form-textarea"
                      placeholder="Describe what you'd like to tell us..."
                      rows={4}
                    />
                  </div>

                  {requestError && (
                    <div className="modal-error">{requestError}</div>
                  )}
                </div>

                <div className="modal-footer">
                  <button 
                    className="modal-cancel-button" 
                    onClick={handleCloseRequestModal}
                    disabled={requestStatus === 'sending'}
                  >
                    Cancel
                  </button>
                  <button 
                    className="modal-send-button" 
                    onClick={handleSendRequest}
                    disabled={requestStatus === 'sending'}
                  >
                    {requestStatus === 'sending' ? 'Sending...' : 'Send Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Volunteer History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={handleCloseHistoryModal}>
          <div className="modal-content history-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header history-modal-header">
              <h2>Volunteer History</h2>
              <button className="modal-close" onClick={handleCloseHistoryModal}>×</button>
            </div>

            <div
              className="history-list"
              ref={historyListRef}
              onScroll={handleHistoryScroll}
            >
              {historyError && (
                <div className="history-status history-error">{historyError}</div>
              )}

              {!historyError && historySessions.length === 0 && !historyLoading && (
                <div className="history-status">No volunteer sessions yet.</div>
              )}

              {groupSessionsByDay(historySessions).map((day) => (
                <div key={day.date} className="history-day">
                  <div className="history-date">{day.date}</div>
                  <div className="history-day-entries">
                    {day.sessions.map((session) => (
                      <div key={session.id} className="history-entry">
                        <span className="history-hours">{formatHours(session)}</span>
                        <span className="history-sep">·</span>
                        <span className="history-times">{formatSessionTimes(session)}</span>
                        <span className="history-sep">·</span>
                        <span className="history-position">
                          {session.task_name} · {formatWorkLocation(session.work_location)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {historyLoading && (
                <div className="history-status">Loading...</div>
              )}

              {!historyLoading && !historyError && historySessions.length > 0 && !historyHasMore && (
                <div className="history-status history-end">
                  Showing all {historyTotal} session{historyTotal === 1 ? '' : 's'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessPage;
